import React, { useEffect, useState } from "react";
import { s3Upload } from "../../../../Common/lib/Session";
import FlakeID from "flake-idgen";
import intformat from "biguint-format";
import Select from "react-select";
import { API } from "../../../../Common/lib/api";
import { getUserSession } from "../../../../Common/lib/Session";
import { useHistory } from "react-router-dom";

export default function GPXUpload(props) {
  const [session, setSession] = useState();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [projects, setProjects] = useState([]);
  const [visualizationTitle, setVisualizationTitle] = useState("");
  const [visualizationDesc, setVisualizationDesc] = useState("");
  const [file, setFile] = useState("");
  const [uploadProgress, setUploadProgress] = useState({
    loaded: 0,
    total: 100,
  });
  const [uploadFailed, setUploadFail] = useState(false);
  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.name.endsWith("gpx") || file.name.endsWith("geojson")) {
        setSelectedFile(file);
        setFile("");
      } else {
        setFile("Invalid file format. Please select a valid GPX or GeoJSON file.");
        setSelectedFile(null);
      }
    }
  };

  const handleOptionChange = (selectedOption) => {
    setSelectedOption(selectedOption);
  };

  const handleTitleChange = (event) => {
    setVisualizationTitle(event.target.value);
  };

  const handleDescChange = (event) => {
    setVisualizationDesc(event.target.value);
  };

  const fetchSession = async () => {
    const session = await getUserSession();
    fetchData(session.id);
    setSession(session);
  };

  const fetchData = async (userId) => {
    const params = {
      createdByUserId: userId,
    };
    const projectsResponse = await API.projects.list(params);
    const items = projectsResponse.data.listProjects?.items || projectsResponse.data?.items || [];
    let projectsData = items.filter(project => project.publishStatus !== "archived");
    setProjects(projectsData);
    setLoading(false);
  };

  function flakeID() {
    const flakeID = new FlakeID();
    return intformat(flakeID.next(), "dec");
  }

  /**
   * Converts a number of bytes to the appropriate size format and adds a suffix (KB, MB, GB, TB).
   * @returns Converted number
   */
  function getFileSize(file) {
    const bytes = file.size;

    if (bytes === 0) {
      return "0 Bytes";
    } else {
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + " " + sizes[i];
    }
  }

  const handleUpload = async () => {
    if (selectedFile && selectedOption) {
      setUploading(true);
      try {
        // Get the selected project
        const selectedProject = projects.find((project) => project.id === selectedOption.value || project.id === parseInt(selectedOption.value));

        if (selectedProject) {
          const url = await s3Upload(selectedFile, setUploadProgress, setUploadFail);

          if (uploadFailed) {
            history.push("/visualization/my-files", {
              notificationShow: true,
              notification: {
                status: false,
                title: "Visualization Upload Failed",
                message: `The GPX/GeoJSON visualization added to "${selectedOption.label}" failed to upload.`,
              },
            });

            return;
          }

          // Parse the existing data visualization
          const dataVisualization = selectedProject.dataVisualization
            ? JSON.parse(selectedProject.dataVisualization)
            : [];

          const id = flakeID();
          let size = getFileSize(selectedFile);

          // Create an updated project object with the new visualization data
          const updatedVisualization = {
            title: visualizationTitle,
            desc: visualizationDesc,
            s3Link: url,
            approved: false,
            id: id,
            archived: false,
            size: size,
          };

          if (selectedFile.name.endsWith("geojson")) {
            updatedVisualization.type = "track/geojson";
          } else if (selectedFile.name.endsWith("gpx")) {
            updatedVisualization.type = "track/gpx";
          }

          // Update the project in DynamoDB
          await API.projects.update({
            id: selectedProject.id,
            dataVisualization: JSON.stringify([...dataVisualization, updatedVisualization]),
          });

          // Always create a pending visualization approval log
          await API.logs.create({
            category: "visualizationApproval",
            Date: Date.now(),
            projectTitle: selectedProject.title,
            visualization: JSON.stringify({ id: id, title: visualizationTitle }),
            projectID: String(selectedProject.id),
            userID: session.username,
            status: "pending",
          });

          // Reset the form fields
          setVisualizationTitle("");
          setVisualizationDesc("");
          setSelectedFile(null);
          setSelectedOption(null);

          history.push("/visualization/my-files", {
            notificationShow: true,
            notification: {
              status: true,
              title: "Visualization Added",
              message: `A GPX/GeoJSON visualization has been added to "${selectedOption.label}"`,
            },
          });
        }
      } catch (error) {
        // console.error('Error uploading file:', error);
        history.push("/visualization/my-files", {
          notificationShow: true,
          notification: {
            status: false,
            title: "Visualization Upload Failed",
            message: `The GPX/GeoJSON visualization added to "${selectedOption.label}" failed to upload.`,
          },
        });
      } finally {
        setUploading(false);
      }
    }
  };

  useEffect(() => {
    fetchSession();
  }, []);

  return (
    <div className="container py-8 px-8 mx-0 min-w-full bg-gray-50 shadow rounded-xl border dark:bg-gray-800 dark:border-gray-700">
      {loading ? (
        <p className="text-center dark:text-white">Loading...</p>
      ) : projects.length > 0 ? (
        <>
          <h2 className="text-base font-semibold leading-7 text-gray-900 mb-6 pb-2 border-b border-gray-200 dark:text-white dark:border-gray-700">
            Add a GPX/GeoJSON Visualization
          </h2>
          <div className="mt-6">
            <p className="mb-2 dark:text-white">Project</p>
            <Select
              inputId="project-input"
              value={selectedOption}
              placeholder="Choose a project"
                  options={projects.map((project) => ({
                    value: project.id,
                    label: project.title,
                  }))}
              onChange={handleOptionChange}
            />
          </div>
          <div className="mt-6">
            <p className="mb-2 dark:text-white">Title</p>
            <input
              data-cy="title"
              type="text"
              maxLength={100}
              className="border border-gray-300 rounded-md shadow-sm focus:ring-1 focus:ring-blue-500 outline-none focus:border-blue-500 px-4 py-2 w-full"
              value={visualizationTitle}
              onChange={handleTitleChange}
            />
          </div>
          <div className="mt-6">
            <p className="mb-2 dark:text-white">Description</p>
            <textarea
              className="max-h-96 outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 border border-gray-300 rounded-md shadow-sm px-4 py-2 w-full resize-y"
              data-cy="description"
              maxLength={800}
              rows={6}
              value={visualizationDesc}
              onChange={handleDescChange}
            />
          </div>
          <div className="mt-6">
            <input
              className="text-sm text-slate-500
              file:mr-4 file:py-2 file:px-4 file:rounded-md
              file:border-2 file:border-solid file:border-blue-700 file:text-sm file:font-semibold
              file:bg-white file:text-blue-700
              hover:file:bg-gray-200"
              data-cy="file"
              type="file"
              accept=".gpx, .geojson"
              onChange={handleFileChange}
            />
          </div>
          <div className="mt-6">
            {file}
            <button
              data-cy="upload"
              className="bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
              onClick={handleUpload}
              disabled={
                uploading || !selectedFile || !visualizationTitle || !visualizationDesc || !selectedOption
              }
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>

          <div
            class="mt-4 flex justify-between mb-1"
            style={{ visibility: uploading ? "visible" : "hidden" }}
          >
            <span class="text-base font-medium dark:text-white">Uploading Visualization...</span>
            <span class="text-sm font-medium dark:text-white">
              {((uploadProgress.loaded / uploadProgress.total) * 100).toFixed(2)}%
            </span>
          </div>
          <div
            class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700"
            style={{ visibility: uploading ? "visible" : "hidden" }}
          >
            <div
              class="bg-blue-600 h-2.5 rounded-full dark:text-white"
              style={{ width: `${(uploadProgress.loaded / uploadProgress.total) * 100}%` }}
            ></div>
          </div>
        </>
      ) : (
        <p className="text-center dark:text-white">You have no projects to add visualizations to.</p>
      )}
    </div>
  );
}
